import React, {
	MouseEvent,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import { ToolbarContext } from 'context/toolbar-context/toolbar-context';
import { ActiveLayerContext } from 'context/active-layer-context/active-layer-context';
import { useCanvasOnClick } from 'hooks/use-canvas-on-click';
import { CanvasContext } from 'context/canvas-context/canvas-context';
import { useCanvasOnMove } from 'hooks/use-canvas-on-move';
import { TextEditorContext } from 'context/text-editor-context/text-editor-context';
import { useKeyboard } from 'hooks/use-keyboard';
import { useCanvasOnWheel } from 'hooks/use-canvas-on-wheel';
import { getTransformedPoint } from 'utils/geometry';

type Props = {
	setCanvasRef: (element: HTMLCanvasElement) => void;
};

/**
 * Canvas Component
 */
export const Canvas: React.FC<Props> = ({ setCanvasRef }) => {
	const [isMousePressed, setMousePressed] = useState<boolean>(false);
	const { renderingManager, renderer } = useContext(CanvasContext);
	const { cursor } = useContext(ToolbarContext);
	const { activeLayer } = useContext(ActiveLayerContext);
	const { setEditable } = useContext(TextEditorContext);

	const onZoomStopped = useCallback(() => {
		renderingManager?.reDraw({ force: true });
	}, [renderingManager]);

	useEffect(() => {
		document.addEventListener('zooming-stopped', onZoomStopped);

		return () => {
			document.removeEventListener('zooming-stopped', onZoomStopped);
		};
	}, [renderingManager, onZoomStopped]);

	useEffect(() => {
		window.addEventListener(
			'wheel',
			(event) => {
				event.preventDefault();
			},
			{ passive: false }
		);
	}, []);

	useKeyboard();

	const onClick = useCanvasOnClick();
	const onMove = useCanvasOnMove(isMousePressed);
	const onWheel = useCanvasOnWheel();

	const onDoubleClick = (event: MouseEvent) => {
		event.preventDefault();
		if (activeLayer && renderer) {
			const currentTransformedPosition = getTransformedPoint(
				event.pageX,
				event.pageY,
				renderer.getTransform()
			);
			window.dispatchEvent(
				new CustomEvent('double-click', {
					detail: {
						pageX: event.pageX,
						pageY: event.pageY,
						transformedPageX: currentTransformedPosition.x,
						transformedPageY: currentTransformedPosition.y,
					},
				})
			);

			setEditable(true);
			renderingManager?.reDrawSync({ exceptLayer: activeLayer });
			renderingManager?.draw(activeLayer, { excludeType: 'text' });
		}
	};

	const onMouseDown = (event: MouseEvent) => {
		setMousePressed(true);

		onClick(event);
	};

	const onMouseUp = () => {
		setMousePressed(false);
	};

	const onTouchMove = () => {};

	return (
		<canvas
			ref={setCanvasRef}
			id="canvas"
			onMouseMove={onMove}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			onDoubleClick={onDoubleClick}
			onTouchMove={onTouchMove}
			onWheel={onWheel}
			style={{
				cursor,
			}}
		/>
	);
};
